import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguageContext } from '../contexts/LanguageContext';

interface NewsItem {
  _id: string;
  slug: string;
  title: string;
  subtitle: string;
  content: string;
  imageUrl: string;
  sources?: string[];
  publishedAt: string;
  createdAt: string;
}

const NewsDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage: language } = useLanguageContext();
  const [news, setNews] = useState<NewsItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      fetchNews(slug);
    }
  }, [slug, language]);

  const fetchNews = async (newsSlug: string) => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/news/${newsSlug}?language=${language}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('News not found');
        } else {
          throw new Error('Failed to fetch news');
        }
        return;
      }
      const data = await response.json();
      setNews(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ru' ? 'ru-RU' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatContent = (content: string, sources: string[] = []) => {
    // First, convert escaped newlines to actual newlines and unescape quotes
    const processedContent = content
      .replace(/\\n\\n/g, '\n\n')  // Convert \\n\\n to \n\n
      .replace(/\\n/g, '\n')       // Convert remaining \\n to \n
      .replace(/\\"/g, '"');       // Unescape quotes for proper display
    
    // Split content into paragraphs and format
    return processedContent.split('\n\n').map((paragraph, index) => {
      if (paragraph.trim() === '') return null;
      
      // Process citations in paragraph
      const formatParagraphWithCitations = (text: string) => {
        // Replace [1], [2], etc. with clickable links
        return text.split(/(\[\d+\])/).map((part, partIndex) => {
          const match = part.match(/\[(\d+)\]/);
          if (match) {
            const citationIndex = parseInt(match[1]) - 1;
            const sourceUrl = sources[citationIndex];
            
            if (sourceUrl) {
              return (
                <a
                  key={partIndex}
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-5 h-5 text-xs bg-purple-600 hover:bg-purple-500 text-white rounded-full transition-colors duration-200 mx-1"
                  title={`Source: ${sourceUrl}`}
                >
                  {match[1]}
                </a>
              );
            }
          }
          return part;
        });
      };
      
      // Check if it's a list
      if (paragraph.includes('•')) {
        const lines = paragraph.split('\n');
        const listItems = lines.filter(line => line.includes('•'));
        const beforeList = lines.slice(0, lines.findIndex(line => line.includes('•')));
        
        return (
          <div key={index} className="mb-6">
            {beforeList.length > 0 && (
              <p className="text-gray-300 leading-relaxed mb-4">
                {formatParagraphWithCitations(beforeList.join(' '))}
              </p>
            )}
            <ul className="list-none space-y-2 ml-4">
              {listItems.map((item, itemIndex) => (
                <li key={itemIndex} className="text-gray-300 leading-relaxed flex items-start">
                  <span className="text-purple-400 mr-3 mt-1">•</span>
                  <span>{formatParagraphWithCitations(item.replace('•', '').trim())}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      }
      
      // Regular paragraph
      return (
        <p key={index} className="text-gray-300 leading-relaxed mb-6">
          {formatParagraphWithCitations(paragraph)}
        </p>
      );
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-700 rounded w-1/4 mb-8"></div>
            <div className="h-64 bg-gray-700 rounded mb-8"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-700 rounded"></div>
              <div className="h-4 bg-gray-700 rounded w-5/6"></div>
              <div className="h-4 bg-gray-700 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-red-400 text-xl mb-4">
            {error === 'News not found' ? t('news.not_found') : error}
          </div>
          <button 
            onClick={() => navigate('/news')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg transition-colors duration-200"
          >
            {t('news.back_to_news')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/news')}
          className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-200 mb-8"
        >
          <svg 
            className="mr-2 w-4 h-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 19l-7-7 7-7" 
            />
          </svg>
          {t('news.back_to_news')}
        </button>

        {/* Article Header */}
        <article className="bg-gray-900 rounded-lg overflow-hidden">
          {/* Featured Image */}
          <div className="h-64 md:h-80 overflow-hidden">
            <img
              src={news.imageUrl}
              alt={news.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/new_logo.png';
              }}
            />
          </div>

          {/* Article Content */}
          <div className="p-8 md:p-12">
            {/* Meta Information */}
            <div className="text-sm text-purple-400 mb-4">
              {formatDate(news.publishedAt)}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
              {news.title.replace(/\\"/g, '"')}
            </h1>



            {/* Content */}
            <div className="prose prose-lg max-w-none">
              {formatContent(news.content, news.sources)}
            </div>

            {/* Share Section */}
            <div className="border-t border-gray-700 pt-8 mt-12">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="mb-4 sm:mb-0">
                  <p className="text-gray-400 text-sm">
                    {t('news.published_on')} {formatDate(news.publishedAt)}
                  </p>
                </div>
                
                <div className="flex items-center space-x-4">
                  <span className="text-gray-400 text-sm">{t('news.share')}:</span>
                  
                  {/* Share buttons */}
                  <button
                    onClick={() => {
                      const url = window.location.href;
                      navigator.clipboard.writeText(url);
                      // You could add a toast notification here
                    }}
                    className="text-gray-400 hover:text-white transition-colors duration-200 p-2 rounded-lg hover:bg-gray-800"
                    title={t('news.copy_link')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                  
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(news.title)}&url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors duration-200 p-2 rounded-lg hover:bg-gray-800"
                    title={t('news.share_twitter')}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Related News or Call to Action */}
        <div className="mt-12 text-center">
          <Link
            to="/news"
            className="inline-flex items-center border border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white px-8 py-3 rounded-lg transition-colors duration-200"
          >
            {t('news.see_all_news')}
            <svg 
              className="ml-2 w-4 h-4" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M9 5l7 7-7 7" 
              />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NewsDetail; 